import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Text, Box, LoadingOverlay, Group } from '@mantine/core';
import { ChartWithTimelines } from '.';
import { DateRangeSelector } from './DateRangeSelector';
import { MetricsSelector } from './MetricsSelector';
import { CategorySelector } from './CategorySelector';
import { AppMedWarning } from './AppMedWarning';
import { useParsedDates } from '../utils';
import { useDateRange, useChartData } from '../hooks';
import { t } from 'i18next';
import { CustomDateModal } from './CustomDateModal';
import { observer } from 'mobx-react';
import { inject } from 'mobx-react';
import { PatientStore } from '../../../../global/Store/PatientStore';
import { usePatientJournalEntries } from '../../../../global/entities/Journal/queries/useJournalEntries';
import { useJournals } from '../../../../global/entities/Journal/queries/useJournals';
import { JOURNALS_METRICS_KEY, JOURNAL_CATEGORIES_KEY, getSelectedMetrics, saveSelectedMetrics } from '../utils/localStorage';
import { LabMetricsSelector } from './LabMetricsSelector';
import { useGetLabTests } from '../../../../global/entities/LabTest/queries/useGetLabTests';
import type { ILabTest } from '../../../../global/entities/LabTest/LabTest';
import { CHART_METRIC_COLORS } from '../utils';
import { processLabTestData } from '../utils';
import { groupLabTests, getChartDataHelpers } from '../utils';
import { IJournalEntry, IJournalResponse } from '../../../../global/entities/Journal/Journal';
import { useGetBiomarkers } from '../../../../global/entities/Biomarker/queries/useGetBiomarkers';
import { QUESTION_TYPE_IDS } from '../../../PatientDashboard/PatientJournal/QuestionTypes';

interface TransformedChartDataPoint {
  FormattedDate: string;
  ShortDate: string;
  [key: string]: string | number | null;
}

/**
 * Formats a Date as a datetime key: YYYY-MM-DDTHH:MM (local time)
 */
function formatDateTimeKey(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateKey = date.toISOString().split('T')[0];
  return `${dateKey}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Transforms raw journal entry data into a chart-friendly format for presence/absence display.
 * Days with multiple entries use datetime keys (YYYY-MM-DDTHH:MM) so each entry is displayed separately.
 */
function transformJournalData(
  journalEntries: IJournalEntry[] = [],
  labTests: ILabTest[] = [],
  startTime: string,
  endTime: string,
  questionToCategoryMap: Record<number, number> = {},
  selectedCategories: string[] = [],
  includeNumericSeries: boolean = false,
  selectedPkMetrics: string[] = [],
  selectedExternalMetrics: string[] = [],
  pkBiomarkerIds: string[] = []
) {
  const dataByDate: Record<string, TransformedChartDataPoint> = {};
  const { addValue, formatDateKey } = getChartDataHelpers(dataByDate);

  // Track metrics that have numeric data
  const numericMetrics = new Set<string>();

  // --- Pass 1: Detect which days have multiple completed entries ---
  const completedEntries = journalEntries.filter(e => e.isCompleted && e.entryDate);
  const entriesByDay: Record<string, IJournalEntry[]> = {};
  completedEntries.forEach(entry => {
    const dayKey = new Date(entry.entryDate).toISOString().split('T')[0];
    if (!entriesByDay[dayKey]) entriesByDay[dayKey] = [];
    entriesByDay[dayKey].push(entry);
  });

  const multiEntryDays = new Set<string>();
  Object.entries(entriesByDay).forEach(([dayKey, entries]) => {
    if (entries.length > 1)
      multiEntryDays.add(dayKey);
  });

  // --- Pass 2: Process entries with appropriate keys ---
  completedEntries.forEach(entry => {
    const dateObj = new Date(entry.entryDate);
    const dayKey = dateObj.toISOString().split('T')[0];

    // Use datetime key for multi-entry days, date-only key otherwise
    let dateKey: string;
    let shortDate: string;
    if (multiEntryDays.has(dayKey)) {
      dateKey = formatDateTimeKey(dateObj);
      const pad = (n: number) => n.toString().padStart(2, '0');
      shortDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        + ` ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
    } else {
      dateKey = dayKey;
      shortDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Ensure the data point exists
    if (!dataByDate[dateKey]) {
      dataByDate[dateKey] = {
        FormattedDate: dateKey,
        ShortDate: shortDate
      };
    }

    // Process all answered responses
    entry.responses.forEach(response => {
      if (response.responseValue && !response.isSkipped) {
        const questionCategoryId = questionToCategoryMap[response.journalQuestionID];
        const shouldInclude = selectedCategories.length > 0 &&
          selectedCategories.includes(questionCategoryId?.toString());

        if (shouldInclude) {
          // Skip displaying circle when both primary and secondary questions are "no"
          const isConditionalMultiselect = response.questionTypeID === QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT;
          const isPrimaryNo = response.responseValue?.toString().toLowerCase() === 'no' || response.responseValue?.toString().toLowerCase() === 'non';
          const isSecondaryNo = response.subResponseValue?.toString().toLowerCase() === 'no' || response.subResponseValue?.toString().toLowerCase() === 'non';
          if (isConditionalMultiselect && isPrimaryNo && isSecondaryNo)
            return;

          const key = `journal-${entry.journalID}-${response.journalQuestionID}`;
          const valueKey = `${key}___value`;
          const questionKey = `${key}___question`;

          const primaryNumericValue = response.responseValue ? parseFloat(response.responseValue) : null;
          const subNumericValue = response.subResponseValue ? parseFloat(response.subResponseValue) : null;
          const isPrimaryNumeric = primaryNumericValue !== null && !isNaN(primaryNumericValue);
          const isSubNumeric = subNumericValue !== null && !isNaN(subNumericValue);
          const numericValue = isSubNumeric ? subNumericValue : (isPrimaryNumeric ? primaryNumericValue : null);
          const isNumeric = numericValue !== null;

          dataByDate[dateKey][key] = 1;

          let displayValue = response.responseValue;
          if (response.responseValue && !isNumeric)
            displayValue = t('journal.' + response.responseValue.toString().toLowerCase());

          dataByDate[dateKey][valueKey] = isNumeric ? numericValue : displayValue + (response.subResponseValue ? ' / ' + response.subResponseValue : '');

          if (isNumeric) {
            numericMetrics.add(key);
            if (includeNumericSeries) {
              const numericKey = `${key}_numeric`;
              dataByDate[dateKey][numericKey] = numericValue;
            }
          }

          const responseText = response.responseValue?.toString().toLowerCase();
          const isYesNoResponse = responseText === 'yes' || responseText === 'no' || responseText === 'oui' || responseText === 'non';
          const isYesResponse = responseText === 'yes' || responseText === 'oui';
          const dotStyleKey = `${key}___dotStyle`;
          dataByDate[dateKey][dotStyleKey] = isYesNoResponse ? (isYesResponse ? 'filled' : 'empty') : 'default';

          const questionText = response.questionText;
          dataByDate[dateKey][questionKey] = questionText;
        }
      }
    });
  });

  // Process lab test data
  const selectedLabMetrics = [
    ...selectedPkMetrics,
    ...selectedExternalMetrics
  ];

  processLabTestData({
    labTests,
    startTime,
    endTime,
    selectedLabMetrics,
    pkBiomarkerIds,
    addValue,
    formatDateKey,
  });

  const sortedData = Object.values(dataByDate).sort(
    (a, b) => new Date(a.FormattedDate).getTime() - new Date(b.FormattedDate).getTime()
  );

  return { data: sortedData, numericMetrics };
}

/**
 * Groups an entry's answered (not skipped) responses by question. Keyed by question ID, not text:
 * a question of a deleted category can share its text with its live copy.
 */
function groupAnsweredResponsesByQuestion(entry: IJournalEntry): Map<number, IJournalResponse[]> {
  const responsesByQuestion = new Map<number, IJournalResponse[]>();
  entry.responses
    .filter(response => !response.isSkipped && response.responseValue)
    .forEach(response => {
      responsesByQuestion.set(response.journalQuestionID, [...(responsesByQuestion.get(response.journalQuestionID) ?? []), response]);
    });
  return responsesByQuestion;
}

/**
 * Groups journals for display as metrics - includes all answered questions
 */
function groupJournalMetrics(
  journalEntries: IJournalEntry[] = [], 
  questionToCategoryMap: Record<number, number> = {},
  selectedCategories: string[] = [],
  includeNumericSeries: boolean = false,
  numericMetricsSet: Set<string> = new Set()
) {
  const uniqueMetrics = new Map();
  
  // First pass: collect all potential metric keys in a stable order for consistent color assignment
  const allMetricKeys: string[] = [];
  
  // Sort journal entries by ID for consistent ordering
  const sortedEntries = [...journalEntries].sort((a, b) => a.journalID - b.journalID);
  
  sortedEntries.forEach((entry) => {
    if (!entry.isCompleted) return;
    
    const journalId = entry.journalID;
    
    const responsesByQuestion = groupAnsweredResponsesByQuestion(entry);
    
    // Sort questions by ID for consistent ordering
    const sortedQuestions = Array.from(responsesByQuestion.entries()).sort((a, b) => {
      const aQuestionId = a[1][0].journalQuestionID;
      const bQuestionId = b[1][0].journalQuestionID;
      return aQuestionId - bQuestionId;
    });
    
    // Collect metric keys in sorted order
    sortedQuestions.forEach(([, responses]) => {
      const firstResponse = responses[0];
      const questionCategoryId = questionToCategoryMap[firstResponse.journalQuestionID];
      const shouldInclude = selectedCategories.length > 0 && 
        selectedCategories.includes(questionCategoryId?.toString());
      
      if (shouldInclude) {
        const metricKey = `journal-${journalId}-${firstResponse.journalQuestionID}`;
        allMetricKeys.push(metricKey);
        
        // Add numeric key if it has numeric data
        if (includeNumericSeries && numericMetricsSet.has(metricKey)) 
          allMetricKeys.push(`${metricKey}_numeric`);
        
      }
    });
  });
  
  // Second pass: create metrics with stable color indices
  sortedEntries.forEach((entry) => {
    if (!entry.isCompleted) return;
    
    const journalId = entry.journalID;
    const journalName = entry.journalName;
    
    const responsesByQuestion = groupAnsweredResponsesByQuestion(entry);
    
    // Create metrics for each question type
    responsesByQuestion.forEach((responses) => {
      const firstResponse = responses[0];
      const questionText = firstResponse.questionText;
      const metricKey = `journal-${journalId}-${firstResponse.journalQuestionID}`;
      
      // Check if this question belongs to a selected category (if categories are filtered)
      const questionCategoryId = questionToCategoryMap[firstResponse.journalQuestionID];
      const shouldInclude = selectedCategories.length > 0 && 
        selectedCategories.includes(questionCategoryId?.toString());
      
      if (shouldInclude) {
        // Add regular metric for dots - use stable color index
        if (!uniqueMetrics.has(metricKey)) {
          const colorIndex = allMetricKeys.indexOf(metricKey);
          uniqueMetrics.set(metricKey, {
            id: metricKey,
            journalId: journalId.toString(),
            name: `${journalName} - ${questionText}`,
            display: questionText.length > 30 ? questionText.substring(0, 30) + '...' : questionText,
            color: CHART_METRIC_COLORS[colorIndex % CHART_METRIC_COLORS.length],
            description: `${journalName} - ${questionText}`,
            isJournal: true,
            questionText: questionText, // Store full question text for display beside chart
          });
        }
        
        // Add numeric series metric if requested and this metric has numeric data
        if (includeNumericSeries && numericMetricsSet.has(metricKey)) {
          const numericKey = `${metricKey}_numeric`;
          if (!uniqueMetrics.has(numericKey)) {
            const colorIndex = allMetricKeys.indexOf(numericKey);
            uniqueMetrics.set(numericKey, {
              id: numericKey,
              journalId: journalId.toString(),
              name: `${journalName} - ${questionText} (Values)`,
              display: `${questionText} (Values)`.length > 30 ? `${questionText} (Values)`.substring(0, 30) + '...' : `${questionText} (Values)`,
              color: CHART_METRIC_COLORS[colorIndex % CHART_METRIC_COLORS.length],
              description: `${journalName} - ${questionText} (Numeric Values)`,
              isJournal: false, // Treat as line metric like lab tests
              isNumericJournal: true, // Special flag to identify numeric journal series
              questionText: questionText,
            });
          }
        }
      }
    });
  });
  
  return Array.from(uniqueMetrics.values());
}

export type IJournalTabProps = {
  patientStore?: PatientStore;
};

export const JournalTab: React.FC<IJournalTabProps> = inject(
  'patientStore',
  'routeStore',
)(
  observer(({ patientStore }) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [journalMetricsOptions, setJournalMetricsOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);
  const [lockedMetrics, setLockedMetrics] = useState<Set<string>>(new Set());
  const [metricsWithNumericData, setMetricsWithNumericData] = useState<Set<string>>(new Set());
  const patientId = patientStore?.patientId;

  const [selectedPkMetrics, setSelectedPkMetrics] = useState<string[]>([]);
  const [selectedExternalMetrics, setSelectedExternalMetrics] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const { data: fetchedLabTests, isLoading: labTestsLoading } = useGetLabTests(patientStore?.patientId);
  const { data: biomarkers } = useGetBiomarkers();
  const labTests = useMemo(() => [...(fetchedLabTests || [])], [fetchedLabTests]);
  const labMetrics = useMemo(() => groupLabTests(labTests), [labTests]);

  // Calculate PK biomarker IDs for time averaging
  const pkBiomarkerIds = useMemo(() => {
    if (!biomarkers) return [];
    return biomarkers
      .filter(biomarker => biomarker.isPK)
      .map(biomarker => `lab-${biomarker.id}`);
  }, [biomarkers]);

  // Fetch journal entries for this patient
  const { data: journalEntries, isLoading: journalEntriesLoading } = usePatientJournalEntries(patientId ? parseInt(patientId) : 0);
  
  // Fetch journal definitions to get category information
  const { data: journalDefinitions, isLoading: journalDefinitionsLoading } = useJournals();
  
  // Filter for completed entries only
  const completedJournalEntries = useMemo(() => {
    return (journalEntries || []).filter(entry => entry.isCompleted);
  }, [journalEntries]);

  // Determine if we're still loading critical data
  const isLoading = journalEntriesLoading || journalDefinitionsLoading || labTestsLoading;

  // Create question-to-category mapping
  const questionToCategoryMap = useMemo(() => {
    const map: Record<number, number> = {};
    if (journalDefinitions) {
      journalDefinitions.forEach(journal => {
        if (journal.categories) {
          journal.categories.forEach(category => {
            if (category.questions) {
              category.questions.forEach(question => {
                map[question.id] = category.id;
              });
            }
          });
        }
      });
    }
    return map;
  }, [journalDefinitions]);

  const chartMargins = { top: 5, right: 30, left: 20, bottom: 5 };
  
  // Base journal metrics (for dots) - these should have stable colors
  const baseJournalMetrics = useMemo(() => {
    const metrics = groupJournalMetrics(completedJournalEntries, questionToCategoryMap, selectedCategories, false, new Set());
    return metrics;
  }, [completedJournalEntries, questionToCategoryMap, selectedCategories]);

  // Combined metrics including numeric series when hovering or locked
  const journalMetrics = useMemo(() => {
    const combinedMetrics = [...baseJournalMetrics];
    
    // Add numeric metrics for both hovered and locked metrics
    const metricsToAddNumeric = new Set<string>();
    
    if (hoveredMetric && metricsWithNumericData.has(hoveredMetric)) 
      metricsToAddNumeric.add(hoveredMetric);
    
    lockedMetrics.forEach(metricId => {
      if (metricsWithNumericData.has(metricId)) 
        metricsToAddNumeric.add(metricId);
      
    });
    
    metricsToAddNumeric.forEach(metricId => {
      const baseMetric = baseJournalMetrics.find(m => m.id === metricId);
      if (baseMetric) {
        const numericKey = `${metricId}_numeric`;
        // Use the same color as the base metric for consistency
        combinedMetrics.push({
          id: numericKey,
          journalId: baseMetric.journalId,
          name: `${baseMetric.name} (Values)`,
          display: `${baseMetric.display} (Values)`,
          color: baseMetric.color, // Same color as base metric
          description: `${baseMetric.description} (Numeric Values)`,
          isJournal: false, // Treat as line metric like lab tests
          isNumericJournal: true,
          questionText: baseMetric.questionText,
        });
      }
    });
    
    return combinedMetrics;
  }, [baseJournalMetrics, hoveredMetric, lockedMetrics, metricsWithNumericData]);

  const transformedChartData = useMemo(
    () => transformJournalData(completedJournalEntries, labTests, startTime, endTime, questionToCategoryMap, selectedCategories, hoveredMetric !== null || lockedMetrics.size > 0, selectedPkMetrics, selectedExternalMetrics, pkBiomarkerIds),
    [completedJournalEntries, labTests, startTime, endTime, questionToCategoryMap, selectedCategories, hoveredMetric, lockedMetrics, selectedPkMetrics, selectedExternalMetrics, pkBiomarkerIds]
  );

  // Detect metrics with numeric data
  useEffect(() => {
    if (transformedChartData.numericMetrics) 
      setMetricsWithNumericData(transformedChartData.numericMetrics);
    
  }, [transformedChartData.numericMetrics]);

  const parsedDates = useParsedDates(transformedChartData.data);

  // Set initial metrics options and category options
  useEffect(() => {
    if (!completedJournalEntries.length) return;
    
    // Get unique journals that have completed entries
    const uniqueJournals = Array.from(
      new Map(
        completedJournalEntries.map(entry => [entry.journalID, entry])
      ).values()
    );
    
    const journalOptions = uniqueJournals.map(entry => ({
      value: entry.journalID.toString(),
      label: entry.journalName
    }));
    
    setJournalMetricsOptions(journalOptions);
  }, [completedJournalEntries]);

  // Build category options from journal definitions and selected journals
  useEffect(() => {
    if (!journalDefinitions || !selectedMetrics.length) {
      setCategoryOptions([]);
      return;
    }

    const categoryOptionsMap = new Map();
    
    // Get categories from selected journals only
    journalDefinitions
      .filter(journal => selectedMetrics.includes(journal.id.toString()))
      .forEach(journal => {
        if (journal.categories) {
          journal.categories.forEach(category => {
            const key = `${category.id}`;
            if (!categoryOptionsMap.has(key)) {
              categoryOptionsMap.set(key, {
                value: key,
                label: category.nameEn || category.nameFr || `Category ${category.id}`, // Use appropriate language
              });
            }
          });
        }
      });

    setCategoryOptions(Array.from(categoryOptionsMap.values()));
  }, [journalDefinitions, selectedMetrics]);

  // Set initial selected metrics when options are loaded
  useEffect(() => {
    if (journalMetricsOptions.length > 0 && selectedMetrics.length === 0) {
      const savedMetrics = getSelectedMetrics(JOURNALS_METRICS_KEY);
      if (savedMetrics.length > 0) {
        // Filter out any saved metrics that no longer exist
        const validSavedMetrics = savedMetrics.filter(metricId => 
          journalMetricsOptions.some(metric => metric.value.toString() === metricId)
        );
        if (validSavedMetrics.length > 0) 
          setSelectedMetrics(validSavedMetrics);
         else 
          setSelectedMetrics([journalMetricsOptions[0].value.toString()]);
        
      } else {
        // Select first available journal by default
        setSelectedMetrics([journalMetricsOptions[0].value.toString()]);
      }
    }
  }, [journalMetricsOptions, selectedMetrics.length]);

  // Set initial selected categories when options are loaded
  useEffect(() => {
    if (categoryOptions.length > 0) {
      const savedCategories = getSelectedMetrics(JOURNAL_CATEGORIES_KEY);
      if (savedCategories.length > 0) {
        // Filter out any saved categories that no longer exist
        const validSavedCategories = savedCategories.filter(categoryId => 
          categoryOptions.some(category => category.value === categoryId)
        );
        setSelectedCategories(validSavedCategories.length > 0 ? validSavedCategories : [categoryOptions[0].value]);
      } else {
        // Select first category by default when no saved categories
        setSelectedCategories([categoryOptions[0].value]);
      }
    } else {
      // Clear categories when no options available
      setSelectedCategories([]);
    }
  }, [categoryOptions]);

  const {
    dateRangePreset,
    customDateRange,
    setShowCustomDateModal,
    showCustomDateModal,
    dateRange,
    handleDateRangeChange,
    handleCustomDateChange,
    dateRangePresets
  } = useDateRange({ parsedDates });

  const {
    processedChartData,
    allDates,
    getDatePosition,
  } = useChartData({
    chartData: transformedChartData.data,
    dateRange,
    parsedDates
  });
  
  const dateRangeDuration = dateRange.endDate && dateRange.startDate ? 
    Math.floor((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const xAxisFormat = dateRangeDuration > 90 ? 'month' : 'day';

  const handleMetricsChange = (selected: string[]) => {
    setSelectedMetrics(selected);
    saveSelectedMetrics(JOURNALS_METRICS_KEY, selected);
  };

  const handleCategoriesChange = (selected: string[]) => {
    setSelectedCategories(selected);
    saveSelectedMetrics(JOURNAL_CATEGORIES_KEY, selected);
  };

  const handleLabelClick = (metricId: string) => {
    setLockedMetrics(prevLocked => {
      const newLocked = new Set(prevLocked);
      if (newLocked.has(metricId)) 
        newLocked.delete(metricId);
       else 
        newLocked.add(metricId);
      
      return newLocked;
    });
  };

  // Filter metrics for selected journals
  const selectedMetricsWithDetails = useMemo(() => {
    const regularMetrics = journalMetrics
      .filter(metric => metric.isJournal && selectedMetrics.includes(metric.journalId))
      .map(metric => metric.id);
    
    // Always include all regular metrics to keep labels visible and avoid shifting
    const metricsToShow = [...regularMetrics];
    
    // Add numeric metrics for both hovered and locked metrics
    const metricsToAddNumeric = new Set<string>();
    
    // When hovering a specific label, add its numeric metric
    if (hoveredMetric !== null) 
      metricsToAddNumeric.add(hoveredMetric);
    
    // Add all locked metrics
    lockedMetrics.forEach(metricId => {
      metricsToAddNumeric.add(metricId);
    });
    
    // Add the numeric versions of these metrics
    metricsToAddNumeric.forEach(metricId => {
      const numericMetricId = `${metricId}_numeric`;
      const numericMetric = journalMetrics.find(metric => metric.id === numericMetricId);
      
      if (numericMetric) 
        metricsToShow.push(numericMetricId);
      
    });
    
    return metricsToShow;
      }, [journalMetrics, selectedMetrics, hoveredMetric, lockedMetrics]);
  
  return (
    <Box style={{ position: 'relative', minHeight: '400px' }}>
      <LoadingOverlay 
        visible={isLoading} 
        overlayProps={{ 
          radius: 'sm', 
          blur: 2,
          backgroundOpacity: 0.85 
        }} 
      />
      <Stack gap="md">
      <AppMedWarning />
        <Group align="flex-start" gap="lg">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="md">
              <DateRangeSelector
                dateRangePreset={dateRangePreset}
                onDateRangeChange={handleDateRangeChange}
                customDateRange={customDateRange}
                setShowCustomDateModal={setShowCustomDateModal}
                dateRangePresets={dateRangePresets}
              />
              
              <MetricsSelector
                metrics={journalMetricsOptions}
                selectedMetrics={selectedMetrics}
                onChange={handleMetricsChange}
                label={t('graph.selectJournals')}
                placeholderKey="graph.healthJournalsSelected"
              />
              
              <CategorySelector
                categories={categoryOptions}
                selectedCategories={selectedCategories}
                onChange={handleCategoriesChange}
                disabled={selectedMetrics.length === 0}
              />
              
              <LabMetricsSelector
                selectedPkMetrics={selectedPkMetrics}
                onPkMetricsChange={setSelectedPkMetrics}
                selectedExternalMetrics={selectedExternalMetrics}
                onExternalMetricsChange={setSelectedExternalMetrics}
                startTime={startTime}
                endTime={endTime}
                onStartTimeChange={setStartTime}
                onEndTimeChange={setEndTime}
              />
            </Stack>
          </Box>
        </Group>
        
        <CustomDateModal
          opened={showCustomDateModal}
          onClose={() => setShowCustomDateModal(false)}
          dateRange={customDateRange}
          onDateChange={handleCustomDateChange}
        />
        
        {/* Show indicator when numeric values are available */}
        {metricsWithNumericData.size > 0 && (
          <Box
            style={{
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            <Text size="sm" >
              {t('graph.hoverForDetails')}
            </Text>
          </Box>
        )}
        
        <ChartWithTimelines 
          data={processedChartData}
          metrics={[...journalMetrics, ...labMetrics]}
          selectedMetrics={[
            ...selectedMetricsWithDetails, 
            ...selectedPkMetrics,
            ...selectedExternalMetrics
          ]}
          chartMargins={chartMargins}
          getDatePosition={getDatePosition}
          allDates={allDates}
          xAxisDataKey="FormattedDate"
          xAxisFormat={xAxisFormat}
          yAxisOrientation="right"
          lineWeight={3}
          onLabelHover={setHoveredMetric}
          onLabelClick={handleLabelClick}
          metricsWithNumericData={metricsWithNumericData}
          hoveredMetric={hoveredMetric}
          lockedMetrics={lockedMetrics}
          showLeftYAxis={true}
        />
      </Stack>
    </Box>
  );
  })
); 